import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { GraduationCap, ArrowRight } from "lucide-react";

/**
 * FeederUniStrip — top-5 feeder universities into `{slug}` rendered
 * as a compact horizontal row on `/employers/{slug}` so families see
 * the pipeline as a visual thread without scrolling into the LEO
 * table. Each pill exposes two targets:
 *   • logo + uni name  → `/uni/{university_id}` (uni profile)
 *   • "See course →"   → `/degree/{degree_id}`  (the specific degree
 *     that fed this firm — the highest-signal sighting)
 *
 * Falls back to a graduation-cap chip when the uni has no domain or
 * the favicon 404s. Orphan feeder rows (uni missing from the
 * `universities` collection) are filtered server-side so pill links
 * never 404.
 */
export default function FeederUniStrip({ slug }) {
  const [rows, setRows] = useState(null);
  const [honestyNote, setHonestyNote] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/employers/${slug}/feeder-unis?limit=15`)
      .then((r) => {
        if (cancelled) return;
        // Endpoint may return multiple courses per uni — dedupe by
        // university_id so a firm with three Oxford PPE variants shows
        // Oxford once, not three times. Backend already sorts by
        // feeder_score desc, so the first hit per uni is the strongest.
        const raw = r.data?.feeder_unis || [];
        const seen = new Set();
        const unique = [];
        for (const u of raw) {
          if (u.university_id && !seen.has(u.university_id)) {
            seen.add(u.university_id);
            unique.push(u);
            if (unique.length >= 5) break;
          }
        }
        // If dedupe collapsed the diversification, re-inject one
        // non-primary-country pick from the tail so the visual
        // matches the "one firm, many offices" methodology.
        const countries = new Set(unique.map((u) => u.country).filter(Boolean));
        if (countries.size <= 1 && raw.length > unique.length) {
          const primary = unique[0]?.country;
          for (const cand of raw) {
            if (
              cand.university_id &&
              !seen.has(cand.university_id) &&
              cand.country &&
              cand.country !== primary
            ) {
              // Drop the weakest primary-country pick to make room.
              if (unique.length >= 5) unique.pop();
              unique.push(cand);
              seen.add(cand.university_id);
              break;
            }
          }
        }
        setRows(unique);
        setHonestyNote(r.data?.honesty_note || null);
      })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [slug]);

  if (err || (rows && rows.length === 0)) return null;
  if (!rows) {
    return (
      <div
        className="mt-6 h-14 rounded-lg border border-stone-200 bg-stone-50/50 animate-pulse"
        data-testid="feeder-uni-strip-loading"
      />
    );
  }

  return (
    <section
      className="mt-6 border border-stone-200 rounded-lg bg-white p-4"
      data-testid="feeder-uni-strip"
    >
      <div className="flex items-center gap-1.5 label-uppercase text-stone-600 mb-3">
        <GraduationCap className="w-3 h-3" />
        Top {rows.length} feeder universities
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {rows.map((u) => (
          <UniPill key={u.university_id} uni={u} />
        ))}
      </div>
      {honestyNote && (
        <p
          className="mt-3 text-[12px] text-stone-600 leading-relaxed border-t border-stone-100 pt-3"
          data-testid="feeder-uni-honesty-note"
        >
          <span className="font-underground-sc text-[10px] tracking-wider text-stone-500">
            Why this order:
          </span>{" "}
          {honestyNote}
        </p>
      )}
    </section>
  );
}

function UniPill({ uni }) {
  const [failed, setFailed] = useState(false);
  const domain = _domainFrom(uni.university_website);
  const hasCourse = Boolean(uni.degree_id);
  const flag = _flagFor(uni.country);
  return (
    <div
      className="group inline-flex items-center gap-2 rounded-full border border-stone-200 hover:border-[#4C1D95] hover:bg-[#F5F0FF] pl-1 pr-1.5 py-1 transition"
      data-testid={`feeder-uni-pill-${uni.university_id}`}
    >
      <Link
        to={`/uni/${uni.university_id}`}
        className="inline-flex items-center gap-2 pr-1"
        data-testid={`feeder-uni-link-${uni.university_id}`}
        title={`View ${uni.university_name || uni.university_id}`}
      >
        {domain && !failed ? (
          <img
            src={`https://icons.duckduckgo.com/ip3/${domain}.ico`}
            alt=""
            width={24}
            height={24}
            loading="lazy"
            onError={() => setFailed(true)}
            onLoad={(e) => { if (e.currentTarget.naturalWidth < 20) setFailed(true); }}
            className="w-6 h-6 rounded object-contain bg-white"
          />
        ) : (
          <span className="w-6 h-6 rounded bg-[#F5F0FF] text-[#4C1D95] inline-flex items-center justify-center">
            <GraduationCap className="w-3 h-3" />
          </span>
        )}
        <span className="text-sm font-medium text-stone-800 group-hover:text-[#4C1D95]">
          {uni.university_name || uni.university_id}
        </span>
        {flag && (
          <span
            className="text-[13px] leading-none"
            title={uni.country}
            aria-label={uni.country}
            data-testid={`feeder-uni-flag-${uni.university_id}`}
          >
            {flag}
          </span>
        )}
      </Link>
      {hasCourse ? (
        <Link
          to={`/degree/${uni.degree_id}`}
          className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-[#F5F0FF] text-[#4C1D95] text-[11px] font-semibold px-2 py-0.5 hover:bg-[#4C1D95] hover:text-white transition"
          data-testid={`feeder-uni-see-course-${uni.university_id}`}
          title={uni.degree_title || `${uni.subject_area} at ${uni.university_name}`}
        >
          See course
          <ArrowRight className="w-3 h-3" />
        </Link>
      ) : (
        <span className="ml-1 text-[11px] text-stone-400 px-1.5">{uni.subject_area}</span>
      )}
    </div>
  );
}

function _domainFrom(website) {
  if (!website) return null;
  try {
    const u = new URL(website.startsWith("http") ? website : `https://${website}`);
    return u.hostname.replace(/^www\./, "").toLowerCase() || null;
  } catch { return null; }
}

// Minimal country → flag emoji lookup for the feeder-uni pills.
// Kept in-component (not a shared util) because this is the only
// caller — extract if a second widget needs it.
const _FLAG_BY_COUNTRY = {
  "UK": "🇬🇧",
  "United Kingdom": "🇬🇧",
  "US": "🇺🇸",
  "USA": "🇺🇸",
  "United States": "🇺🇸",
  "Canada": "🇨🇦",
  "Australia": "🇦🇺",
  "Germany": "🇩🇪",
  "France": "🇫🇷",
  "Italy": "🇮🇹",
  "Spain": "🇪🇸",
  "Netherlands": "🇳🇱",
  "Belgium": "🇧🇪",
  "Ireland": "🇮🇪",
  "Switzerland": "🇨🇭",
  "Austria": "🇦🇹",
  "Portugal": "🇵🇹",
  "Denmark": "🇩🇰",
  "Sweden": "🇸🇪",
  "Norway": "🇳🇴",
  "Finland": "🇫🇮",
  "India": "🇮🇳",
  "Singapore": "🇸🇬",
  "Hong Kong": "🇭🇰",
  "Japan": "🇯🇵",
  "South Korea": "🇰🇷",
  "United Arab Emirates": "🇦🇪",
  "UAE": "🇦🇪",
};

function _flagFor(country) {
  if (!country) return null;
  return _FLAG_BY_COUNTRY[country] || null;
}
